import React, {
  useState,
  useEffect,
  createContext,
  useContext,
  useCallback,
} from "react";

import {
  BrowserRouter as Router,
  Routes,
  Route,
  Link,
  useNavigate,
  useLocation,
  NavLink,
} from "react-router-dom";


import { MOCK_EVENTS, DECADES } from "./constants.js";
import { ThemeContextType, SecurityEvent } from "./types.js";
import Home from "./pages/Home.js";
import DecadesPage from "./pages/DecadesPage.js";
import YearsPage from "./pages/YearsPage.js";
import EventsPage from "./pages/EventsPage.js";
import EventDetailPage from "./pages/EventDetailPage.js";
import ToolkitPage from "./pages/ToolkitPage.js";
import SearchResults from "./pages/SearchResults.js";
import BlogIndex from "./pages/BlogIndex.tsx";
import AboutUs from "./pages/AboutUs.tsx";
import ContactUs from "./pages/ContactUs.tsx";
import NotFoundPage from "./pages/NotFoundPage.tsx";
import PrivacyPolicy from "./pages/PrivacyPolicy.tsx";
import TermsOfService from "./pages/TermsOfService.tsx";
import WhatIsCyberSecurity from "./pages/blog/WhatIsCyberSecurity.tsx";
import VPNSecurityGuide from "./pages/blog/VPNSecurityGuide.tsx";
import VPNPrivacyProtection from "./pages/blog/VPNPrivacyProtection.tsx";
import VPNSafetyAndSecurity from "./pages/blog/VPNSafetyAndSecurity.tsx";
import AIPoweredVPNs from "./pages/blog/AIPoweredVPNs.tsx";
import ZeroTrustVPNs from "./pages/blog/ZeroTrustVPNs.tsx";
import HistoryOfCybercrime from "./pages/blog/HistoryOfCybercrime.tsx";
import Top10CyberAttacks from "./pages/blog/Top10CyberAttacks.tsx";
import CommonCyberAttacks from "./pages/blog/CommonCyberAttacks.tsx";
import CybersecurityBestPractices from "./pages/blog/CybersecurityBestPractices.tsx";
import EvolutionOfCybersecurity from "./pages/blog/EvolutionOfCybersecurity.tsx";
import WhatIsFirewall from "./pages/blog/WhatIsFirewall.tsx";
import WhatIsVPN from "./pages/blog/WhatIsVPN.tsx";
import CyberAttacks2025 from "./pages/blog/CyberAttacks2025.tsx";
import MostPopularCyberAttacks from "./pages/blog/MostPopularCyberAttacks.tsx";
import WorstCyberAttacks from "./pages/blog/WorstCyberAttacks.tsx";
import AIDrivenCyberAttacks from "./pages/blog/AIDrivenCyberAttacks.tsx";
import ZeroTrustArchitecture from "./pages/blog/ZeroTrustArchitecture.tsx";
import RansomwareAsAService from "./pages/blog/RansomwareAsAService.tsx";
import DeepfakesSyntheticIdentity from "./pages/blog/DeepfakesSyntheticIdentity.tsx";
import CloudSecuritySolutions from "./pages/blog/CloudSecuritySolutions.tsx";
import PostQuantumCryptography from "./pages/blog/PostQuantumCryptography.tsx";
import EveryTypeOfCyberAttack from "./pages/blog/EveryTypeOfCyberAttack.tsx";
import FirstCyberCrime1820 from "./pages/blog/FirstCyberCrime1820.tsx";
import TopBiggestCyberAttacks from "./pages/blog/TopBiggestCyberAttacks.tsx";
// import TheEvolutionofMalware from "./pages/blog/TheEvolutionofMalware.tsx";
// import TheBiggestHackInUSHistory from "./pages/blog/TheBiggestHackInUSHistory.tsx";
// import TheMostDestructiveHack from "./pages/blog/TheMostDestructiveHack.tsx";
import ThreatIntelligenceIndex from "./pages/blog/ThreatIntelligenceIndex.tsx";
import DefeattheQuantumMenace from "./pages/blog/DefeattheQuantumMenace.tsx";
// import HackingHistory from "./pages/blog/hackingHistory.tsx";
import CybersecurityTrendsIn2026 from "./pages/blog/CybersecurityTrendsIn2026.tsx";
// import ThreatsVulnerabilitiesandExploits from "./pages/blog/ThreatsVulnerabilitiesandExploits.tsx";
import WannaCry from "./pages/blog/WannaCry.tsx";
import ZeroClickAttacks from "./pages/blog/ZeroClickAttacks.tsx";
import CyberAttackCategories from "./pages/blog/CyberAttackCategories.tsx";
import NetworkBasedAttacks from "./pages/blog/Category/NetworkBasedAttacks.tsx";
import WebApplicationAttacks from "./pages/blog/Category/WebApplicationAttacks.tsx";
import SocialEngineeringAttacks from "./pages/blog/Category/SocialEngineeringAttacks.tsx";
import MalwareAttacks from "./pages/blog/Category/MalwareAttacks.tsx";
import AuthenticationAttacks from "./pages/blog/Category/AuthenticationAttacks.tsx";
import PhysicalAttacks from "./pages/blog/Category/PhysicalAttacks.tsx";
import CryptographicAttacks from "./pages/blog/Category/CryptographicAttacks.tsx";
import WirelessIoTAttacks from "./pages/blog/Category/WirelessIoTAttacks.tsx";
import AdvancedAttacks from "./pages/blog/Category/AdvancedAttacks.tsx";
import { ThemeProvider } from "./components/ThemeProvider";
import PhishingAttack from "./pages/blog/Attack/phishing.tsx";
import YouTube141YearVideo from "./pages/blog/YouTube141YearVideo.tsx";
import GeoNewsHackIncident from "./pages/blog/GeoNewsHackIncident.tsx";
import VercelBreachIncident from "./pages/blog/VercelBreachIncident2026.tsx";
import GitHubBreachIncident from "./pages/blog/GitHubBreachIncident.tsx";

export const useArchiveContext = () => useContext(ArchiveContext);

// Scroll to top on every route change
const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

export const ArchiveContext = createContext<{
  eraCounts: Record<string, number>;
  yearCounts: Record<number, number>;
  isLoading: boolean;
  getEraCounts: () => Promise<void>;
  getYearCounts: (decade: string) => Promise<void>;
  getEventsByYear: (year: number) => Promise<SecurityEvent[]>;
  getEventById: (id: string) => Promise<SecurityEvent | null>;
  refreshArchive: () => Promise<void>;
}>({
  eraCounts: {},
  yearCounts: {},
  isLoading: false,
  getEraCounts: async () => {},
  getYearCounts: async () => {},
  getEventsByYear: async () => [],
  getEventById: async () => null,
  refreshArchive: async () => {},
});

const Logo = () => (
  <div className="flex items-center gap-2 sm:gap-3 group shrink-0">
    <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-xl overflow-hidden bg-slate-900 flex items-center justify-center shadow-lg shadow-blue-500/20 transition-transform group-hover:scale-105 border border-white/10">
      <img
        src="/cyberAnnals_logo.png"
        alt="CyberAnnals Logo"
        className="w-full h-full object-cover"
        loading="eager"
        fetchPriority="high"
        width="40"
        height="40"
        onError={(e) => {
          const target = e.target as HTMLImageElement;
          target.parentElement!.classList.add("bg-blue-600");
          target.style.display = "none";
          // Show a fallback icon if image fails
          const icon = document.createElement("i");
          icon.className =
            "fa-solid fa-shield-halved text-white text-xs sm:text-sm";
          target.parentElement?.appendChild(icon);
        }}
      />
    </div>
    <span className="text-base sm:text-xl font-black bg-gradient-to-r from-white to-slate-500 bg-clip-text text-transparent tracking-tighter uppercase whitespace-nowrap">
      CyberAnnals
    </span>
  </div>
);

const Layout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchSuggestions, setSearchSuggestions] = useState<any[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const handleSearchInput = async (value: string) => {
    if (value.length < 2) {
      setSearchSuggestions([]);
      return;
    }

    try {
      const response = await fetch(
        `/api/events/search/suggestions?q=${encodeURIComponent(value)}&limit=5`
      );
      if (response.ok) {
        const data = await response.json();
        setSearchSuggestions(data);
        setShowSuggestions(true);
      }
    } catch (error) {
      console.error("Search error:", error);
    }
  };

  useEffect(() => {
    document.body.style.overflow = isMenuOpen ? "hidden" : "auto";
  }, [isMenuOpen]);

  useEffect(() => {
    setIsMenuOpen(false);
  }, [location]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const query = searchQuery.trim();
    if (!query) return;

    // Year Pattern Check
    const yearMatch = query.match(/^(\d+)\s*(bce)?$/i);
    if (yearMatch) {
      const year = parseInt(yearMatch[1]);
      const isBCE = !!yearMatch[2];
      const yearNum = isBCE ? -year : year;
      navigate(`/year/${yearNum}`);
    } else {
      // Direct Search Navigation
      navigate(`/search?q=${encodeURIComponent(query)}`);
    }
    setSearchQuery("");
    setSearchSuggestions([]);
    setShowSuggestions(false);
  };

  const navItemClass = ({ isActive }: { isActive: boolean }) =>
    `transition-colors duration-500 hover:text-blue-400 ${
      isActive ? "text-blue-500" : "text-slate-400"
    }`;

  return (
    <div className="min-h-screen flex flex-col font-sans antialiased">
      <ScrollToTop />
      <nav className="sticky top-0 z-[100] bg-slate-900/80 backdrop-blur-xl border-b border-gray-700 h-16 sm:h-20 flex items-center">
        <div className="max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center gap-4">
            <Link to="/" className="shrink-0" aria-label="CyberAnnals Home">
              <Logo />
            </Link>
            <div className="hidden lg:flex items-center gap-8 flex-grow justify-end">
              <form
                onSubmit={handleSearch}
                className="relative group max-w-xs w-full"
              >
                <input
                  type="text"
                  placeholder="Search events, years, attackers..."
                  className="w-full bg-gray-700/30 border border-gray-600/50 rounded-full px-5 py-1.5 text-[10px] font-bold uppercase tracking-wider focus:ring-1 focus:ring-purple-500 outline-none transition-all placeholder:text-gray-400"
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    handleSearchInput(e.target.value);
                  }}
                  onFocus={() =>
                    searchQuery.length >= 2 && setShowSuggestions(true)
                  }
                  onBlur={() =>
                    setTimeout(() => setShowSuggestions(false), 200)
                  }
                />

                {/* SEARCH SUGGESTIONS DROPDOWN */}
                {showSuggestions && searchSuggestions.length > 0 && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-gray-800 border border-gray-700 rounded-lg shadow-xl z-50 max-h-60 overflow-y-auto">
                    {searchSuggestions.map((suggestion, idx) => (
                      <Link
                        key={suggestion.id}
                        to={suggestion.url}
                        className="flex items-center gap-3 p-3 hover:bg-gray-700 border-b border-gray-700 last:border-0 transition-colors"
                        onClick={() => {
                          setShowSuggestions(false);
                          setSearchQuery("");
                          setSearchSuggestions([]);
                        }}
                      >
                        <div className="flex-1">
                          <div className="text-xs font-bold text-white truncate">
                            {suggestion.title}
                          </div>
                          <div className="text-[10px] text-slate-400 mt-0.5">
                            {suggestion.year} • {suggestion.type} •{" "}
                            {suggestion.victim}
                          </div>
                        </div>
                        <i className="fa-solid fa-arrow-right text-[10px] text-blue-400"></i>
                      </Link>
                    ))}
                  </div>
                )}

                <button
                  type="submit"
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white transition-colors"
                >
                  <i className="fa-solid fa-search text-[10px]"></i>
                </button>
              </form>
              <div className="flex items-center gap-8 text-[10px] font-black uppercase tracking-[0.2em]">
                <NavLink to="/" className={navItemClass}>
                  Home
                </NavLink>
                <NavLink to="/decades" className={navItemClass}>
                  History
                </NavLink>
                <NavLink to="/blog" className={navItemClass}>
                  Blog
                </NavLink>
                <NavLink to="/about" className={navItemClass}>
                  About
                </NavLink>
                <NavLink to="/contact" className={navItemClass}>
                  Contact
                </NavLink>
                <NavLink to="/toolkit" className={navItemClass}>
                  Toolkit
                </NavLink>
              </div>
            </div>
            <div className="lg:hidden flex items-center">
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="w-10 h-10 flex items-center justify-center rounded-xl bg-white/5 border border-white/10 text-white"
              >
                <i
                  className={`fa-solid ${
                    isMenuOpen ? "fa-xmark" : "fa-bars-staggered"
                  } text-lg`}
                ></i>
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Menu */}
      <div
        className={`fixed inset-0 z-[90] bg-gray-900/95 backdrop-blur-2xl transition-opacity duration-300 ${
          isMenuOpen ? "opacity-100" : "opacity-0 pointer-events-none"
        } lg:hidden`}
      >
        <div className="flex flex-col h-full pt-28 pb-12 px-8 gap-8">
          <Link
            to="/"
            className="text-4xl font-black uppercase tracking-tighter italic"
          >
            Home
          </Link>
          <Link
            to="/decades"
            className="text-4xl font-black uppercase tracking-tighter italic"
          >
            History
          </Link>
          <Link
            to="/blog"
            className="text-4xl font-black uppercase tracking-tighter italic"
          >
            Blog
          </Link>
          <Link
            to="/about"
            className="text-4xl font-black uppercase tracking-tighter italic"
          >
            About
          </Link>
          <Link
            to="/contact"
            className="text-4xl font-black uppercase tracking-tighter italic"
          >
            Contact
          </Link>
          <Link
            to="/toolkit"
            className="text-4xl font-black uppercase tracking-tighter italic"
          >
            Toolkit
          </Link>
        </div>
      </div>

      <main className="flex-grow max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-8">
        {children}
      </main>
      <footer className="border-t border-white/5 py-16 bg-slate-950">
        <div className="max-w-7xl mx-auto px-4 flex flex-col md:flex-row justify-between items-center gap-8">
          {/* Left Section - Logo and Tagline */}
          <div className="space-y-4 text-center md:text-left">
            <Logo />
            <p className="text-[9px] font-black uppercase tracking-widest text-slate-500 max-w-xs">
              Global Historical Archive of Cyber Attacks.
            </p>
          </div>

          {/* Center Section - Contact and Feedback */}
          <div className="flex flex-col items-center space-y-4">
            <span className="text-[16px] font-black font-mono uppercase tracking-[0.1em] text-blue-500 underline underline-offset-8 decoration-blue-300/10">
              Contact & Feedback
            </span>
            <div className="flex gap-4">
              {[
                { icon: "fa-brands fa-github", url: "https://github.com/muhammadhussain" },
                { icon: "fa-brands fa-linkedin-in", url: "http://www.linkedin.com/in/muhammad-hussain456" },
                { icon: "fa-brands fa-twitter", url: "https://x.com/muhammadhu7ain" },
                { icon: "fa-solid fa-globe", url: "https://muhammadhussain" },
                { icon: "fa-brands fa-youtube", url: "https://youtube.com/" },
                { icon: "fa-solid fa-envelope", url: "/contact" },
              ].map((social, index) => (
                <a
                  key={index}
                  href={social.url}
                  className="text-[11px] font-bold uppercase tracking-widest text-slate-400 hover:text-blue-400 transition-all flex items-center gap-4 group"
                >
                  <div className="w-8 h-8 rounded-lg bg-white/5 border border-white/5 group-hover:border-blue-500/50 flex items-center justify-center transition-all">
                    <i
                      className={`${social.icon.includes('fa-') ? social.icon : `fa-brands ${social.icon}`} text-slate-600 group-hover:text-blue-500 transition-colors`}
                    ></i>
                  </div>
                </a>
              ))}
            </div>
          </div>

          {/* Right Section - Legal Links and Copyright */}
          <div className="flex flex-col items-center md:items-end gap-2">
            <div className="flex gap-4 text-[10px] font-bold uppercase tracking-widest text-slate-400">
              <a href="/privacy-policy" className="hover:text-blue-400 transition-colors">Privacy Policy</a>
              <a href="/terms-of-service" className="hover:text-blue-400 transition-colors">Terms of Service</a>
            </div>
            <p className="text-[9px] font-black uppercase tracking-[0.2em] text-slate-600">
              © 2025 CyberAnnals Archive
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

const App: React.FC = () => {
  const [eraCounts, setEraCounts] = useState<Record<string, number>>({});
  const [yearCounts, setYearCounts] = useState<Record<number, number>>({});
  const [isLoading, setIsLoading] = useState(false);

  const getEraCounts = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await fetch("/api/events/counts/decades");
      if (response.ok) {
        const data = await response.json();
        console.log("[API] Era counts:", data);
        setEraCounts(data);
      } else {
        console.warn("[API] Era counts failed, using mock data");
        const counts: Record<string, number> = {};
        DECADES.forEach((d) => {
          counts[d] = MOCK_EVENTS.filter((e) => e.decade === d).length;
        });
        setEraCounts(counts);
      }
    } catch (error) {
      console.error("[API] Era counts error:", error);
      const counts: Record<string, number> = {};
      DECADES.forEach(
        (d) => (counts[d] = MOCK_EVENTS.filter((e) => e.decade === d).length)
      );
      setEraCounts(counts);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const getYearCounts = useCallback(async (decade: string) => {
    setIsLoading(true);
    try {
      const response = await fetch(
        `/api/events/counts/years?decade=${encodeURIComponent(decade)}`
      );
      if (response.ok) {
        const data = await response.json();
        console.log(`[API] Year counts for ${decade}:`, data);
        setYearCounts(data);
      } else {
        console.warn(`[API] Year counts for ${decade} failed, using mock data`);
        const counts: Record<number, number> = {};
        MOCK_EVENTS.filter((e) => e.decade === decade).forEach((e) => {
          counts[e.eventYear] = (counts[e.eventYear] || 0) + 1;
        });
        setYearCounts(counts);
      }
    } catch (error) {
      console.error(`[API] Year counts for ${decade} error:`, error);
      const counts: Record<number, number> = {};
      MOCK_EVENTS.filter((e) => e.decade === decade).forEach((e) => {
        counts[e.eventYear] = (counts[e.eventYear] || 0) + 1;
      });
      setYearCounts(counts);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const getEventsByYear = useCallback(
    async (year: number): Promise<SecurityEvent[]> => {
      setIsLoading(true);
      try {
        console.log(`[API DEBUG] Fetching events for year: ${year}`);
        const response = await fetch(`/api/events?year=${year}`);

        console.log(`[API DEBUG] Response status: ${response.status}`);
        console.log(
          `[API DEBUG] Content-Type:`,
          response.headers.get("content-type")
        );

        if (response.ok) {
          const contentType = response.headers.get("content-type");

          // Check if response is HTML (error page) instead of JSON
          if (contentType && contentType.includes("text/html")) {
            const htmlText = await response.text();
            console.error(
              `[API ERROR] Got HTML instead of JSON for year ${year}:`,
              htmlText.substring(0, 200)
            );
            throw new Error("API returned HTML error page");
          }

          const data = await response.json();
          console.log(
            `[API DEBUG] Got ${data.length} events from API for year ${year}`
          );
          return data.map((e: any) => ({
            ...e,
            victim:
              typeof e.victim === "string" ? JSON.parse(e.victim) : e.victim,
          }));
        } else {
          // Try to get error text
          const errorText = await response.text();
          console.error(
            `[API ERROR] API failed (${response.status}):`,
            errorText.substring(0, 500)
          );

          // If it's HTML, show more info
          if (errorText.includes("<!DOCTYPE") || errorText.includes("<html")) {
            console.error(`[API ERROR] HTML Error Page Detected!`);
            console.error(
              `[API ERROR] Full URL: https://${window.location.host}/api/events?year=${year}`
            );
          }

          return MOCK_EVENTS.filter((e) => e.eventYear === year);
        }
      } catch (error) {
        console.error(`[API ERROR] Fetch failed for year ${year}:`, error);
        return MOCK_EVENTS.filter((e) => e.eventYear === year);
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  const getEventById = useCallback(
    async (id: string): Promise<SecurityEvent | null> => {
      setIsLoading(true);
      try {
        console.log(`[API DEBUG] Fetching event with ID: ${id}`);
        const response = await fetch(`/api/events/${id}`);

        if (response.ok) {
          const data = await response.json();
          console.log(`[API DEBUG] Got event:`, data.title);
          return data;
        } else {
          console.warn(`[API] Event ${id} not found in API, using mock data`);
          return MOCK_EVENTS.find((e) => e.id === id) || null;
        }
      } catch (error) {
        console.error(`[API ERROR] Fetch event ${id} failed:`, error);
        return MOCK_EVENTS.find((e) => e.id === id) || null;
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  const refreshArchive = useCallback(async () => {
    await getEraCounts();
  }, [getEraCounts]);

  // Initialize era counts on app load
  useEffect(() => {
    console.log("[APP] Initializing archive context");
    refreshArchive();
  }, [refreshArchive]);

  return (
    <ThemeProvider>
      <ArchiveContext.Provider
        value={{
          eraCounts,
          yearCounts,
          isLoading,
          getEraCounts,
          getYearCounts,
          getEventsByYear,
          getEventById,
          refreshArchive,
        }}
      >
        <Router>
          <Layout>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/decades" element={<DecadesPage />} />
              <Route path="/decade/:decadeName" element={<YearsPage />} />
              <Route path="/year/:yearNum" element={<EventsPage />} />
              <Route path="/event/:eventId" element={<EventDetailPage />} />
              <Route path="/search" element={<SearchResults />} />
              <Route path="/blog" element={<BlogIndex />} />
              <Route
                path="/blog/what-is-cyber-security"
                element={<WhatIsCyberSecurity />}
              />
              <Route
                path="/blog/history-of-cybercrime"
                element={<HistoryOfCybercrime />}
              />
              <Route
                path="/blog/top-10-cyber-attacks"
                element={<Top10CyberAttacks />}
              />
              <Route
                path="/blog/common-cyber-attacks"
                element={<CommonCyberAttacks />}
              />
              <Route
                path="/blog/cybersecurity-best-practices"
                element={<CybersecurityBestPractices />}
              />
              <Route
                path="/blog/evolution-of-cybersecurity"
                element={<EvolutionOfCybersecurity />}
              />
              <Route
                path="/blog/what-is-firewall"
                element={<WhatIsFirewall />}
              />
              <Route path="/blog/what-is-vpn" element={<WhatIsVPN />} />
              <Route
                path="/blog/cyber-attacks-2025"
                element={<CyberAttacks2025 />}
              />
              <Route
                path="/blog/most-popular-cyber-attacks"
                element={<MostPopularCyberAttacks />}
              />
              <Route
                path="/blog/worst-cyber-attacks"
                element={<WorstCyberAttacks />}
              />
              <Route path="/blog/vpn-security-guide" element={<VPNSecurityGuide />} />
              <Route path="/blog/vpn-privacy-protection" element={<VPNPrivacyProtection />} />
              <Route path="/blog/vpn-safety-and-security" element={<VPNSafetyAndSecurity />} />
              <Route path="/blog/ai-powered-vpns" element={<AIPoweredVPNs />} />
              <Route path="/blog/zero-trust-vpns" element={<ZeroTrustVPNs />} />
              <Route path="/blog/ai-driven-cyber-attacks" element={<AIDrivenCyberAttacks />} />
              <Route path="/blog/zero-trust-architecture" element={<ZeroTrustArchitecture />} />
              <Route path="/blog/ransomware-as-a-service" element={<RansomwareAsAService />} />
              <Route path="/blog/deepfakes-synthetic-identity" element={<DeepfakesSyntheticIdentity />} />
              <Route path="/blog/cloud-security-solutions" element={<CloudSecuritySolutions />} />
              <Route path="/blog/post-quantum-cryptography" element={<PostQuantumCryptography />} />
              <Route path="/blog/every-type-of-cyber-attack" element={<EveryTypeOfCyberAttack />} />
              <Route path="/blog/first-cyber-crime-1820" element={<FirstCyberCrime1820 />} />
              <Route path="/blog/top-biggest-cyber-attacks" element={<TopBiggestCyberAttacks />} />
              {/* <Route path="/blog/recently-hacked-companies" element={<RecentlyHackedCompanies />} />
              <Route path="/blog/the-evolution-of-malware" element={<TheEvolutionofMalware />} />
              <Route path="/blog/the-biggest-hack-in-us-history" element={<TheBiggestHackInUSHistory />} />
              <Route path="/blog/the-most-destructive-hack" element={<TheMostDestructiveHack />} /> */}
              <Route path="/blog/threat-intelligence-index-2025" element={<ThreatIntelligenceIndex />} />
              <Route path="/blog/defeat-the-quantum-menace" element={<DefeattheQuantumMenace />} />
              {/* <Route path="/blog/hacking-history" element={<HackingHistory />} /> */}
              <Route path="/blog/cybersecurity-trends-in-2026" element={<CybersecurityTrendsIn2026 />} />
              {/* <Route path="/blog/threats-vulnerabilities-and-exploits" element={<ThreatsVulnerabilitiesandExploits />} /> */}
              <Route path="/blog/wannacry" element={<WannaCry />} />
              <Route path="/blog/zero-click-attacks" element={<ZeroClickAttacks />} />
              <Route path="/blog/cyber-attack-categories" element={<CyberAttackCategories />} />
              <Route path="/blog/category/network-based" element={<NetworkBasedAttacks />} />
              <Route path="/blog/category/web-application" element={<WebApplicationAttacks />} />
              <Route path="/blog/category/social-engineering" element={<SocialEngineeringAttacks />} />
              <Route path="/blog/category/malware" element={<MalwareAttacks />} />
              <Route path="/blog/category/authentication" element={<AuthenticationAttacks />} />
              <Route path="/blog/category/physical" element={<PhysicalAttacks />} />
              <Route path="/blog/category/cryptographic" element={<CryptographicAttacks />} />
              <Route path="/blog/category/wireless-iot" element={<WirelessIoTAttacks />} />
              <Route path="/blog/category/advanced" element={<AdvancedAttacks />} />
              <Route path="/toolkit" element={<ToolkitPage />} />
              <Route path="/about" element={<AboutUs />} />
              <Route path="/contact" element={<ContactUs />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/terms-of-service" element={<TermsOfService />} />
              <Route path="/blog/Attack/phishing" element={<PhishingAttack />} />
              <Route path="/blog/youtube-longest-video" element={<YouTube141YearVideo />} />
              <Route path="/blog/geo-news-hack-incident-2026" element={<GeoNewsHackIncident />} />
              <Route path="/blog/vercel-data-breach-2026" element={<VercelBreachIncident />} />
              <Route path="/blog/github-data-breach-2026" element={<GitHubBreachIncident />} />
              <Route path="*" element={<NotFoundPage />} />

            </Routes>
          </Layout>
        </Router>
      </ArchiveContext.Provider>
    </ThemeProvider>
  );
};

export default App;
